/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import cn from 'classnames'
import { useField } from 'formik'
import RSelect, {
    components,
    Props as PropsOrigin,
    OptionTypeBase,
    ControlProps,
    IndicatorProps
} from 'react-select'
import AsyncSelect, { AsyncProps as AsyncSelectProps } from 'react-select/async'
import * as R from 'ramda'
import { drawCaret, hideVisually } from '../../../styles/mixins'

// app components
import CheckOption, { CheckOptionProps } from './CheckOption'

// type definition
export type OptionTypeDefault = { label: string; value: any }
export interface SelectProps<OptionType extends OptionTypeBase = OptionTypeDefault>
    extends PropsOrigin<OptionType>,
        Omit<AsyncSelectProps<OptionType>, 'loadOptions'> {
    name: string
    type?: string
    multiple?: boolean
    dropdown?: boolean
    disabled?: boolean
    iconDrop?: boolean
    isAsync?: boolean
    loadOptionsData?: AsyncSelectProps<OptionType>['loadOptions']
    appTheme?: any // TODO: utilizar el tipo DefaultTheme cuando este definido para el tema de la aplicación,
}

// custom select components
const SelectControl: React.FC<ControlProps<any>> = ({ className, ...restProps }) => {
    const classes = cn(className, { 'form-control': false })
    return <components.Control className={classes} {...restProps} />
}

const DropdownIndicator: React.FC<IndicatorProps<any>> = ({ className, ...restProps }) => (
    <components.DropdownIndicator className={className} {...restProps}>
        <span className="m2-select__caret" />
    </components.DropdownIndicator>
)

// helpers
/**
 * parse value selected from field.value
 *
 * @param {any} valSelected
 * @return {Array<any>}
 */
const _parseValue = (valSelected: any): any[] => {
    if (Array.isArray(valSelected)) {
        return valSelected.map(opt =>
            opt && Object.hasOwnProperty.call(opt, 'value') ? opt.value : opt
        )
    }

    return valSelected || valSelected === 0 ? [valSelected] : []
}

/**
 * Custom select component that composes select component from react-select
 * @param {SelectProps} Props
 * @return {React.ReactNode}
 */
const SelectUnstyled: React.FC<SelectProps> = ({
    name,
    className,
    classNamePrefix,
    multiple,
    dropdown,
    isAsync,
    iconDrop,
    loadOptionsData,
    onChange,
    disabled: isDisabled,
    ...props
}) => {
    // initial state and declarations
    const [field, meta, helpers] = useField({ name, multiple })
    const [menuOpen, setMenuOpen] = useState<boolean | undefined>(undefined)
    const containerRef = useRef<HTMLDivElement | null>(null)

    const rest = R.omit(['onBlur', 'type', 'appTheme'], props)
    const classes = cn(className, 'm2-select', {
        [`m2-select-dropdown`]: !!dropdown,
        [`m2-select-icon-dropdown`]: !!iconDrop
    })
    const classesIn = cn(classNamePrefix, 'm2-select')

    // component lifecycle event
    useEffect(() => {
        // ! this is a bit hackly..!
        if (containerRef.current) {
            const event = new Event('change', { bubbles: true })
            const inputEl = containerRef.current.querySelector(`[name="${field.name}"]:last-child`)

            if (inputEl && typeof meta.value !== 'undefined') {
                inputEl.dispatchEvent(event)
            }
        }
    }, [field.name, meta.value])

    // event handlers
    const handleChange = (value, action): void => {
        const altValue = !Array.isArray(value) ? (value || value === 0 ? [value] : []) : value
        helpers.setValue(altValue)

        if (typeof onChange === 'function') {
            onChange(altValue, action)
        }
    }

    const handleBlur = (): void => {
        helpers.setTouched(true)
    }

    // custom components for react-select
    const customComponents = {
        Control: SelectControl,
        IndicatorSeparator: () => null,
        DropdownIndicator
    }
    if (field.multiple || multiple) {
        const WrapCheckOption: React.FC<Omit<CheckOptionProps, 'setMenuOpen'>> = attrs => (
            <CheckOption setMenuOpen={setMenuOpen} {...attrs} />
        )
        customComponents['Option'] = WrapCheckOption
        customComponents['MultiValueRemove'] = () => null
    }

    // common props to pass it to either Select or AsyncSelect
    const passProps = {
        components: customComponents,
        onChange: handleChange,
        onBlur: handleBlur,
        isMulti: field.multiple || multiple,
        className: classes,
        classNamePrefix: classesIn,
        hideSelectedOptions: false,
        closeMenuOnSelect: !(field.multiple || multiple),
        isClearable: false,
        blurInputOnSelect: false,
        menuIsOpen: menuOpen,
        value: field.value,
        isDisabled
    }

    const parsedValue = _parseValue(field.value)

    // Switch on type of select, either async or normal
    return (
        <div className="m2-select-container" ref={containerRef}>
            {!!isAsync && typeof loadOptionsData === 'function' ? (
                <AsyncSelect loadOptions={loadOptionsData} {...passProps} {...rest} />
            ) : (
                <RSelect isSearchable={false} {...passProps} {...rest} />
            )}
            {parsedValue.length ? (
                parsedValue.map((val, index) => (
                    <input
                        key={`m2-select-value-${index}`}
                        css={hideVisually()}
                        type="hidden"
                        name={field.name}
                        value={val}
                    />
                ))
            ) : (
                <input css={hideVisually()} type="hidden" name={field.name} />
            )}
        </div>
    )
}
// Custom select styles
const Select = styled(SelectUnstyled).attrs(({ theme }) => ({
    appTheme: theme,
    theme: provided => ({
        ...provided,
        borderRadius: !!theme['$enable-rounded'] ? theme['$input-border-radius'] : 0,
        colors: {
            ...provided.colors,
            primary: theme['$primary'],
            danger: theme['$danger']
        },
        spacing: {
            ...provided.spacing,
            controlHeight: 'auto', // theme['$input-height'],
            menuGutter: 0
        }
    }),
    styles: {
        control: (styles, state) => ({
            ...styles,
            borderColor:
                state.hasValue && !state.isFocused
                    ? theme['$strong-gray']
                    : state.isFocused
                    ? theme['$input-focus-border-color']
                    : theme['$input-border-color']
        }),
        input: styles => ({
            ...styles,
            margin: '0 2px',
            paddingBottom: '0',
            paddingTop: '0'
        })
    }
}))`
    &.m2-select {
        .m2-select {
            &__placeholder {
                font-style: italic;
                margin: 0;
            }

            /* Content of select */
            &__control {
                /* height: ${props => props.appTheme['$input-height']}; */
                &:hover {
                    border-color: ${props => props.appTheme['$input-border-color']};
                }
                &--is-focused,
                &--is-focused:hover {
                    border-color: ${props => props.appTheme['$input-focus-border-color']};
                    box-shadow: none;
                }
                &--menu-is-open {
                     .m2-select__dropdown-indicator {
                         .m2-select__caret {
                             ${drawCaret('up')}
                             color: ${props => props.appTheme['$primary']};
                             margin: 0;
                         }
                     }
                 }

            }

            &__value-container {
                line-height: ${props => props.appTheme['$input-line-height']};
                padding: ${props =>
                    `${props.appTheme['$input-padding-y']} ${props.appTheme['$input-padding-x']}`};
                padding-right: ${props => props.appTheme['$spacers'][2]};

                & > input {
                        line-height: ${props => props.appTheme['$input-line-height']};
                    }

                &--is-multi {
                    display: block;
                    white-space: nowrap;

                    & > input {
                        display: inline-block;
                        vertical-align: top;
                    }
                }
            }

            &__indicator {
                padding: ${props => props.appTheme['$spacers'][1]};
            }

            &__dropdown-indicator {
                padding-right: ${props => props.appTheme['$input-padding-x']};

                .m2-select__caret {
                    ${drawCaret('down')}
                    color: ${props => props.appTheme['$primary']};
                    margin: 0;
                }
            }

            /* Menu container of options */
            &__menu {
                min-width: 100%;
                width: auto;
                white-space: nowrap;
                border-radius: 0;
                z-index: 3;
                border-bottom-left-radius: ${props =>
                    !!props.appTheme['$enable-rounded']
                        ? props.appTheme['$input-border-radius']
                        : 0};
                border-bottom-right-radius: ${props =>
                    !!props.appTheme['$enable-rounded']
                        ? props.appTheme['$input-border-radius']
                        : 0};
                &-list {
                    padding-top: 0;
                    padding-bottom: 0;
                }
            }

            /* Option item */
            &__option {
                color: ${props => props.appTheme['$dropdown-color']};
                font-weight: ${props => props.appTheme['$font-weight-medium']};
                line-height: 1.1;
                padding: ${props =>
                    `${props.appTheme['$dropdown-item-padding-y']} ${props.appTheme['$dropdown-item-padding-x']}`};

                &--is-focused,
                &--is-selected {
                    color: ${props => props.appTheme['$dropdown-link-hover-color']};
                    background-color: ${props => props.appTheme['$dropdown-link-hover-bg']};
                }
            }

            &__single-value {
                color: ${props => props.appTheme['$input-color']};
                margin-left: 0px;
            }

            &__multi-value {
                display: inline-block;
                vertical-align: top;
                line-height: ${props => props.appTheme['$input-line-height']};
                background-color: transparent;
                margin: 0;

                &__label {
                    color: ${props => props.appTheme['$input-color']};
                    padding: 0px;
                    padding-right: ${props => props.appTheme['$spacers'][1]};
                    font-size: inherit;
                    line-height: ${props => props.appTheme['$input-line-height']};
                }

                &:not(:nth-last-child(2)) {
                    .m2-select__multi-value__label {
                        &:after {
                            content: ","
                        }
                    }
                }
            }
        }
    }
    /*  */
    /* Validation styles */
    .has-danger & {
        .m2-select {
            &__control,
            &__control:hover {
                border-color: ${props => props.appTheme['$danger']};
            }
        }
    }
    /* Dropodown Select */
    &.m2-select-dropdown {
        .m2-select__control {
            border: none;
            background-color: transparent;
            .m2-select__value-container {
                padding-top:  ${props => props.appTheme['$dropdown-item-padding-y']};
                padding-bottom:  ${props => props.appTheme['$dropdown-item-padding-y']};
            }
            .m2-select__single-value,
            .m2-select__placeholder {
                color: ${props => props.appTheme['$primary']};
                font-weight: ${props => props.appTheme['$font-weight-medium']};
                font-style: normal;
                right: ${props => props.appTheme['$spacers'][2]};
            }
        }
    }
    /* Dropodown Icon Select */
    &.m2-select-icon-dropdown {
        .m2-select__control {
            border: none;
            background-color: transparent;
            .m2-select__value-container {
                padding-top: 0;
                padding-bottom: ${props => props.appTheme['$spacers'][1]};
            }
            .m2-select__indicators {
                display: none;
            }
            .m2-select__single-value,
            .m2-select__placeholder {
                color: ${props => props.appTheme['$blue-links']};
                font-size: ${props => props.appTheme['$text-tiny']};
            }
        }
        .m2-select__menu {
            right: 0;
        }
    }
` as typeof SelectUnstyled

export default Select
